/**
 * Dedicated axios instance for the Wisetap ingest API.
 *
 * CRITICAL: this MUST NOT reuse the shared api-utils interceptors. In this app a
 * 401 from any shared-axios call globally signs the user out, and 412/418
 * resolve as success + `SERVER_UNAVAILABLE` fires a Hindi toast. A fire-and-
 * forget analytics call must never trigger any of that — so its own client,
 * own base URL, all errors swallowed.
 *
 * Auth is header-injectable for when the endpoints require it later ("no auth
 * for now").
 */
import axios from "axios";
import RNConfig from "react-native-config";
import type { OaUserEvent, OaUserProfile } from "./types";

// No hardcoded fallback: an empty EVENTS_URL means the ingest is OFF (e.g.
// non-production builds), and nothing is sent. See isIngestEnabled().
export const EVENTS_URL = RNConfig.EVENTS_URL || "";

/** True only when an ingest URL is configured. Guards all sends. */
export const isIngestEnabled = (): boolean => EVENTS_URL.length > 0;

const client = axios.create({ baseURL: EVENTS_URL, timeout: 8000 });

type HeaderFactory = () => Record<string, string>;

let authHeader: HeaderFactory = () => ({});

/** Inject an auth-header factory when the ingest endpoints start requiring one. */
export const setAuthHeader = (fn: HeaderFactory): void => {
  if (typeof fn === "function") authHeader = fn;
};

/**
 * POST a batch of event rows (202). No-op when the ingest is disabled. Unlike
 * everything else here this does NOT swallow — it lets the promise reject so
 * EventQueue can keep the rows persisted and retry, instead of silently dropping
 * events on a transient network failure.
 */
export const sendEvents = (rows: OaUserEvent[]): Promise<unknown> => {
  if (!isIngestEnabled() || !rows || !rows.length) return Promise.resolve();
  return client.post("/v1/oa/events", rows, { headers: authHeader() });
};

/** POST a single profile object. No-op when disabled; errors swallowed. */
export const postProfile = (profile: OaUserProfile): Promise<unknown> => {
  if (!isIngestEnabled()) return Promise.resolve();
  return client
    .post("/v1/oa/user/profile", profile, { headers: authHeader() })
    .catch(() => {});
};
